import { api } from '../../core/network/api';

/**
 * `GET /account/me` — everything the Settings screen's Account and Papers tabs show.
 *
 * The profile fields (`firstName`, `lastName`, `phone`, `country`) are game-owned columns, added
 * below the schema's GAME MODELS marker rather than to core's `User` — the sibling game put them
 * in its own core and now carries a permanently deviating template, which CLAUDE.md exists to
 * prevent. They are optional on this interface because an older backend simply omits them, and
 * the screen has to render against that rather than crash.
 *
 * `locale`/`currency` are reported but never edited from this screen: Black Line has no frontend
 * i18n layer, so a language or currency switcher would change nothing on screen. See
 * settings-route.ts's Account tab for where that is called out again, next to the code it would
 * otherwise apply to.
 */
export interface AccountInfo {
  email: string;
  username: string | null;
  role: 'PLAYER' | 'ADMIN' | 'KYC_ADMIN' | string;
  createdAt: string;
  emailVerified: boolean;
  locale: string | null;
  currency: string | null;
  /** Given name. Absent from an older backend's payload; null when never set. */
  firstName?: string | null;
  lastName?: string | null;
  /** Contact number. Player-editable, unlike the KYC block's own details. */
  phone?: string | null;
  /** Billing country, ISO 3166-1 alpha-2 — this is the one that drives invoice VAT. Distinct
   * from the KYC applicant's declared country, which is evidence about a person rather than a
   * tax address. */
  country?: string | null;
  kycStatus: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED' | string;
  kycRejectReason: string | null;
  kycSubmittedAt: string | null;
  /** `feature.kyc` game setting — hides the Papers tab's KYC block when off. */
  kycEnabled: boolean;
}

export const fetchAccount = () => api.get<AccountInfo>('/account/me');

/** What `PATCH /account/profile` echoes back — the stored values after the patch, which is what
 * the form re-seeds itself from (a trimmed or cleared field comes back different from what was
 * typed, and the screen must show what was actually saved). */
export interface ProfilePatchResult {
  ok: boolean;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
}

/**
 * `PATCH /account/profile` — partial by design, and the caller must keep it partial.
 *
 * Sending all three fields every time is a real defect the sibling game shipped and fixed: a
 * stored phone number that no longer passes the current format check made saving a *surname*
 * fail on "Invalid phone number", a field the player had not touched. Only send what changed.
 */
export const updateProfile = (patch: {
  firstName?: string;
  lastName?: string;
  phone?: string;
}) => api.patch<ProfilePatchResult>('/account/profile', patch);

/** `PATCH /account/country` — the billing country behind invoice VAT. ISO 3166-1 alpha-2. */
export const setCountry = (country: string) =>
  api.patch<{ ok: boolean; country: string }>('/account/country', { country });

/** `POST /account/change-password` — verifies the current password server-side first. */
export const changePassword = (currentPassword: string, newPassword: string) =>
  api.post<{ ok: boolean }>('/account/change-password', { currentPassword, newPassword });

/** `POST /account/resend-verification` — re-sends the verify-email mail. */
export const resendVerification = () =>
  api.post<{ ok: boolean }>('/account/resend-verification', {});

/**
 * `POST /kyc/submit` — core endpoint, multipart. `country` here is the KYC applicant's declared
 * country, not a stored account preference — there is nowhere on the account to save one.
 */
export function submitKyc(input: {
  fullName: string;
  dateOfBirth: string; // yyyy-mm-dd
  address: string;
  country: string; // ISO-2
  document: File;
}): Promise<{ status: string }> {
  const form = new FormData();
  form.set('fullName', input.fullName);
  form.set('dateOfBirth', input.dateOfBirth);
  form.set('address', input.address);
  form.set('country', input.country);
  form.set('document', input.document);
  // Override the client's JSON default so axios sets the multipart boundary.
  return api.post<{ status: string }>('/kyc/submit', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/** ISO-2 country list, shared by the KYC form and the billing-country picker. Same list the
 * sibling game uses. */
export const COUNTRIES: Array<[string, string]> = [
  ['AT', 'Austria'], ['BE', 'Belgium'], ['BG', 'Bulgaria'], ['HR', 'Croatia'],
  ['CY', 'Cyprus'], ['CZ', 'Czech Republic'], ['DK', 'Denmark'], ['EE', 'Estonia'],
  ['FI', 'Finland'], ['FR', 'France'], ['DE', 'Germany'], ['GR', 'Greece'],
  ['HU', 'Hungary'], ['IE', 'Ireland'], ['IT', 'Italy'], ['LV', 'Latvia'],
  ['LT', 'Lithuania'], ['LU', 'Luxembourg'], ['MT', 'Malta'], ['NL', 'Netherlands'],
  ['PL', 'Poland'], ['PT', 'Portugal'], ['RO', 'Romania'], ['SK', 'Slovakia'],
  ['SI', 'Slovenia'], ['ES', 'Spain'], ['SE', 'Sweden'],
  ['GB', 'United Kingdom'], ['US', 'United States'],
];
